import { apiClient } from './api';

// VERACODE-MITIGATION: CWE-918 - All API requests use the environment-configured base URL
// (VITE_API_BASE_URL) via the shared API client. URLs are not user-controlled.

// Closure-flow errors carry a code so the page handler can map to a
// user-friendly message (SELF_APPROVAL_FORBIDDEN, SUPERVISOR_MISMATCH, etc.)
// instead of falling back to a generic "Action failed" toast.
export class ClosureFlowError extends Error {
  constructor(public readonly code: string | undefined, message: string) {
    super(message);
    this.name = 'ClosureFlowError';
  }
}

export interface Comment {
  id: string;
  incidentId: string;
  userId: string;
  comment: string;
  isInternal: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export interface Attachment {
  id: string;
  incidentId: string;
  filename: string;
  originalFilename: string;
  fileSize: number;
  mimeType: string;
  filePath: string;
  uploadedBy: string;
  uploadedAt: string;
  virusScanStatus: string;
  virusScanResult?: string;
}

export interface IncidentHistory {
  id: string;
  incidentId: string;
  fieldName: string;
  oldValue: string | null;
  newValue: string | null;
  changedBy: string;
  changedAt: string;
  action?: string;
  metadata?: string;
}

export interface Incident {
  id: string;
  incidentNumber: string;
  title: string;
  description: string;
  severity: 'MINIMAL' | 'MODERATE' | 'HIGH';
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'PENDING_CLOSURE_APPROVAL' | 'CLOSED' | 'CANCELLED';
  priority: string;
  category?: string;
  location?: string;
  affectedSystems?: string[];
  tags?: string[];
  reporterName: string;
  reporterEmail: string;
  reporterPhone?: string;
  reporterOrganization?: string;
  assignedTo?: string;
  assignedAt?: string;
  resolvedAt?: string;
  closedAt?: string;
  occurredAt?: string;
  // Supervisor (VIS-55)
  supervisorId?: string;
  supervisorName?: string;
  supervisorEmail?: string;
  // Closure workflow (VIS-55)
  closureRequestedBy?: string;
  closureRequestedAt?: string;
  closureRequestNote?: string;
  closureApprovedBy?: string;
  closureApprovedAt?: string;
  closureApprovalNote?: string;
  closureRejectedBy?: string;
  closureRejectedAt?: string;
  closureRejectionNote?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  // Optional nested objects for display purposes
  reporter?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  assignee?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  attachments?: Attachment[];
  comments?: Comment[];
  history?: IncidentHistory[];
}

export interface IncidentStats {
  total: number;
  open: number;
  inProgress: number;
  resolved: number;
  high: number;
  bySeverity: Record<string, number>;
  byStatus: Record<string, number>;
}

export interface CreateIncidentData {
  title: string;
  description: string;
  severity?: 'MINIMAL' | 'MODERATE' | 'HIGH';
  category?: string;
  location?: string;
  occurredAt?: string;
  affectedSystems?: string[];
  tags?: string[];
  assigneeId?: string;
  supervisor?: { id: string; name: string; email: string };
}

export interface UpdateIncidentData {
  title?: string;
  description?: string;
  severity?: 'MINIMAL' | 'MODERATE' | 'HIGH';
  status?: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'PENDING_CLOSURE_APPROVAL' | 'CLOSED' | 'CANCELLED';
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  category?: string;
  location?: string;
  affectedSystems?: string[];
  tags?: string[];
  assigneeId?: string;
  assignedAt?: string;
  resolvedAt?: string;
  occurredAt?: string | null;
  incidentNumber?: string;
  supervisor?: { id: string; name: string; email: string } | null;
}

export interface GetIncidentsParams {
  page?: number;
  limit?: number;
  severity?: string;
  status?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  supervisorId?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  meta: {
    timestamp: string;
    total?: number;
    page?: number;
    limit?: number;
    totalPages?: number;
  };
  error?: {
    code: string;
    message: string;
  };
}

class IncidentsService {
  /**
   * Get all incidents with filtering and pagination
   */
  async getIncidents(params: GetIncidentsParams = {}): Promise<{
    incidents: Incident[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const queryParams = new URLSearchParams();

    if (params.page) {queryParams.append('page', params.page.toString());}
    if (params.limit) {queryParams.append('limit', params.limit.toString());}
    if (params.severity && params.severity !== 'all') {queryParams.append('severity', params.severity);}
    if (params.status && params.status !== 'all') {queryParams.append('status', params.status);}
    if (params.search) {queryParams.append('search', params.search);}
    if (params.sortBy) {queryParams.append('sortBy', params.sortBy);}
    if (params.sortOrder) {queryParams.append('sortOrder', params.sortOrder);}
    if (params.supervisorId) {queryParams.append('supervisorId', params.supervisorId);}

    const url = `/api/v1/incidents${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    // Define pagination type
    interface PaginationData {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }

    // The API returns data in format: { success: true, data: { incidents: [], pagination: {} } }
    const response = await apiClient.get<{ incidents: Incident[]; pagination: PaginationData }>(url);

    if (response.success && response.data) {
      // Extract incidents and pagination from the nested data structure
      const { incidents, pagination } = response.data;
      return {
        incidents: incidents ?? [],
        total: pagination?.total ?? 0,
        page: pagination?.page ?? 1,
        limit: pagination?.limit ?? 10,
        totalPages: pagination?.totalPages ?? 1
      };
    }
    throw new Error(response.error?.message ?? 'Failed to fetch incidents');
  }

  /**
   * Get incident by ID
   */
  async getIncidentById(id: string): Promise<Incident> {
    const response = await apiClient.get<Incident>(`/api/v1/incidents/${id}`);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to fetch incident');
  }

  /**
   * Create new incident
   */
  async createIncident(data: CreateIncidentData): Promise<Incident> {
    const response = await apiClient.post<Incident>('/api/v1/incidents', data);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to create incident');
  }

  /**
   * Update incident
   */
  async updateIncident(id: string, data: UpdateIncidentData): Promise<Incident> {
    const response = await apiClient.put<Incident>(`/api/v1/incidents/${id}`, data);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to update incident');
  }

  /**
   * Delete incident
   */
  async deleteIncident(id: string): Promise<void> {
    const response = await apiClient.delete<void>(`/api/v1/incidents/${id}`);
    if (!response.success) {
      throw new Error(response.error?.message ?? 'Failed to delete incident');
    }
  }

  /**
   * Get incident statistics
   */
  async getIncidentStats(): Promise<IncidentStats> {
    const response = await apiClient.get<IncidentStats>('/api/v1/incidents/stats');
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to fetch incident statistics');
  }

  /**
   * Add a comment to an incident
   */
  async addComment(incidentId: string, commentText: string, isInternal: boolean = false): Promise<Comment> {
    const response = await apiClient.post<Comment>(`/api/v1/incidents/${incidentId}/comments`, {
      comment: commentText,
      isInternal,
    });
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to add comment');
  }

  /**
   * Upload an attachment to an incident
   */
  async uploadAttachment(incidentId: string, file: File): Promise<Attachment> {
    const response = await apiClient.uploadFile<Attachment>(
      `/api/v1/incidents/${incidentId}/attachments`,
      file
    );

    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to upload attachment');
  }

  /**
   * Delete an attachment
   */
  async deleteAttachment(attachmentId: string): Promise<void> {
    const response = await apiClient.delete<void>(`/api/v1/incidents/attachments/${attachmentId}`);
    if (!response.success) {
      throw new Error(response.error?.message ?? 'Failed to delete attachment');
    }
  }

  /**
   * Get download URL for an attachment
   */
  getAttachmentDownloadUrl(attachmentId: string): string {
    return `${import.meta.env.VITE_API_BASE_URL}/api/v1/incidents/attachments/${attachmentId}/download`;
  }

  /**
   * Get severity display properties
   */
  getSeverityConfig(severity: string) {
    const config = {
      MINIMAL: { color: 'success', label: 'Minimal' },
      MODERATE: { color: 'warning', label: 'Moderate' },
      HIGH: { color: 'danger', label: 'High' }
    };
    return config[severity as keyof typeof config] ?? { color: 'secondary', label: severity };
  }

  /**
   * Get status display properties
   */
  getStatusConfig(status: string) {
    const config = {
      OPEN: { color: 'danger', label: 'Open' },
      IN_PROGRESS: { color: 'warning', label: 'In Progress' },
      RESOLVED: { color: 'success', label: 'Resolved' },
      CLOSED: { color: 'secondary', label: 'Closed' }
    };
    return config[status as keyof typeof config] ?? { color: 'secondary', label: status };
  }

  /**
   * Format timestamp for display
   */
  formatTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) {return 'Just now';}
    if (diffInMinutes < 60) {return `${diffInMinutes} minutes ago`;}

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {return `${diffInHours} hours ago`;}

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {return `${diffInDays} days ago`;}

    return date.toLocaleDateString();
  }

  /**
   * Get user display name
   */
  getUserDisplayName(user?: { firstName: string; lastName: string }) {
    if (!user) {return 'Unassigned';}
    return `${user.firstName} ${user.lastName}`;
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) {return '0 Bytes';}

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Get file type label from mime type
   */
  getFileTypeLabel(mimeType: string): string {
    if (mimeType.startsWith('image/')) {return 'Image';}
    if (mimeType === 'application/pdf') {return 'PDF';}
    if (mimeType.includes('word')) {return 'Word';}
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) {return 'Excel';}
    if (mimeType === 'text/plain') {return 'Text';}
    if (mimeType === 'text/csv') {return 'CSV';}
    return 'File';
  }

  /**
   * Link an investigation to an incident
   */
  async linkInvestigation(incidentId: string, investigationId: string): Promise<{ linkedInvestigationIds: string[] }> {
    const response = await apiClient.post<{ linkedInvestigationIds: string[] }>(
      `/api/v1/incidents/${incidentId}/link-investigation`,
      { investigationId, action: 'link' }
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to link investigation');
  }

  /**
   * Unlink an investigation from an incident
   */
  async unlinkInvestigation(incidentId: string, investigationId: string): Promise<{ linkedInvestigationIds: string[] }> {
    const response = await apiClient.post<{ linkedInvestigationIds: string[] }>(
      `/api/v1/incidents/${incidentId}/link-investigation`,
      { investigationId, action: 'unlink' }
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to unlink investigation');
  }

  /**
   * Request closure approval for an incident (VIS-55)
   */
  async requestClosure(incidentId: string, note?: string): Promise<Incident> {
    const response = await apiClient.post<Incident>(
      `/api/v1/incidents/${incidentId}/closure-requests`,
      { note },
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new ClosureFlowError(response.error?.code, response.error?.message ?? 'Failed to request closure');
  }

  /**
   * Approve a closure request for an incident (VIS-55)
   */
  async approveClosure(incidentId: string, note?: string): Promise<Incident> {
    const response = await apiClient.post<Incident>(
      `/api/v1/incidents/${incidentId}/closure-requests/approve`,
      { note },
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new ClosureFlowError(response.error?.code, response.error?.message ?? 'Failed to approve closure');
  }

  /**
   * Reject a closure request for an incident (VIS-55)
   */
  async rejectClosure(incidentId: string, note: string): Promise<Incident> {
    const response = await apiClient.post<Incident>(
      `/api/v1/incidents/${incidentId}/closure-requests/reject`,
      { note },
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new ClosureFlowError(response.error?.code, response.error?.message ?? 'Failed to reject closure');
  }

  /**
   * Withdraw a pending closure request for an incident (VIS-55)
   */
  async withdrawClosure(incidentId: string): Promise<Incident> {
    const response = await apiClient.post<Incident>(
      `/api/v1/incidents/${incidentId}/closure-requests/withdraw`,
      {},
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new ClosureFlowError(response.error?.code, response.error?.message ?? 'Failed to withdraw closure request');
  }
}

export const incidentsService = new IncidentsService();
export default incidentsService;